import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, ChevronDown, ListFilter, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import {
  workflowInsightsApi,
  type CompanyInsights,
  type WorkflowInsightRow,
} from "@/api/workflow-insights";
import { KpiStrip, type KpiItem } from "@/components/ui/kpi-strip";
import { AgentAvatarStack } from "@/components/agent/agent-avatar";
import { withWorkspaceContext } from "@/lib/workspace-navigation";
import {
  formatCostCents,
  formatDateRange,
  formatDurationMs,
  formatHours,
  formatInt,
  formatPercent,
  formatRelativeTime,
  formatSignedPercent,
  formatTokens,
} from "@/lib/format";
import {
  InsightsRangePicker,
  rangeKey,
  rangeQuery,
  rangeSearch,
  useInsightsRange,
  type InsightsRangeValue,
} from "./range-picker";
import { RunsPerDayChart } from "./runs-per-day-chart";
import { triggerLabel } from "./trigger-label";

/**
 * The Workflows view (design 5a / 5c): run metrics for a period, then one row
 * per workflow. Since 2026-09-30 this is the whole Workflows screen; the old
 * "All" list was folded in (Kenneth: the two felt the same).
 *
 * Every workflow in scope is listed, including ones with no runs. The host
 * decides what a row click does (the canvas) and supplies each row's menu,
 * where "View insights" opens the per-workflow Insights page.
 *
 * The window lives in the URL (`?period=`, or `?since=&until=` once someone
 * picks their own dates, plus `?compare=`) so a link carries it; the agent
 * filter, search and sort are local.
 */
export function WorkflowInsightsView({
  agentId: scopedAgentId,
  rowDates,
  onOpenWorkflow,
  renderRowMenu,
  emptyState,
}: {
  agentId: string | null;
  /** Each workflow's created and last-edited times by id, shown under its
   *  name. "Updated" means the workflow or a prompt changed, not a run. */
  rowDates?: Map<string, { createdAt: string; updatedAt: string }>;
  /** Row click. Defaults to the workflow's Insights page. */
  onOpenWorkflow?: (workflowId: string) => void;
  /** The menu at the end of each row. */
  renderRowMenu?: (row: WorkflowInsightRow, openInsights: () => void) => ReactNode;
  /** Shown instead of the table when there are no workflows at all. */
  emptyState?: ReactNode;
}) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [range, setRange] = useInsightsRange();

  // The workspace's agent scope wins; otherwise the header dropdown filters.
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const effectiveAgentId = scopedAgentId ?? agentFilter;
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const agentById = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["workflow-insights", tenantId, rangeKey(range), effectiveAgentId ?? "all"],
    queryFn: () => workflowInsightsApi.company(tenantId, rangeQuery(range), effectiveAgentId),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  type SortCol = "name" | "runs" | "successRate" | "avgDurationMs" | "tokens" | "lastRunAt";
  const [sort, setSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>({ col: "runs", dir: "desc" });
  const toggleSort = (col: SortCol) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "name" ? "asc" : "desc" }));

  // Search filters by name only, the same rule the old All list used.
  const [search, setSearch] = useState("");
  const searchTerm = search.trim().toLowerCase();
  // The Filter button: by status, and by when the workflow was created.
  const [filters, setFilters] = useState<WorkflowFilters>(NO_FILTERS);
  const filterCount =
    (filters.status !== "all" ? 1 : 0) + (filters.createdWithinDays !== null ? 1 : 0);

  const rows = useMemo(() => {
    const createdCutoff =
      filters.createdWithinDays !== null ? Date.now() - filters.createdWithinDays * 86_400_000 : null;
    const list = (data?.workflows ?? []).filter((w) => {
      if (searchTerm && !w.name.toLowerCase().includes(searchTerm)) return false;
      if (filters.status !== "all" && w.status !== filters.status) return false;
      if (createdCutoff !== null) {
        const created = rowDates?.get(w.id)?.createdAt;
        if (!created || new Date(created).getTime() < createdCutoff) return false;
      }
      return true;
    });
    const val = (w: WorkflowInsightRow): number | string => {
      switch (sort.col) {
        case "name": return w.name.toLowerCase();
        case "runs": return w.runs;
        case "successRate": return w.successRate ?? -1;
        case "avgDurationMs": return w.avgDurationMs ?? -1;
        case "tokens": return w.tokensIn + w.tokensOut;
        case "lastRunAt": return w.lastRunAt ? new Date(w.lastRunAt).getTime() : 0;
      }
    };
    list.sort((a, b) => {
      const x = val(a), y = val(b);
      const cmp = typeof x === "string" && typeof y === "string" ? x.localeCompare(y) : (x as number) - (y as number);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [data?.workflows, sort, searchTerm, filters, rowDates]);

  const kpis = buildKpis(data, range);

  const openInsights = (id: string) =>
    navigate(withWorkspaceContext(`/tenants/${tenantId}/workflows/${id}/insights?${rangeSearch(range)}`, searchParams));
  const openWorkflow = (id: string) => (onOpenWorkflow ? onOpenWorkflow(id) : openInsights(id));
  const hasNoWorkflows = !!data && data.workflows.length === 0;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Controls row. Right-aligned in the same centred column as the header
          above (max-w-[1216px], px-6 lg:px-8), so its right edge lines up with
          New Workflow. Export was removed on 2026-09-30 (Kenneth). */}
      <div className="shrink-0 border-b border-border">
        <div className="mx-auto flex w-full max-w-[1216px] flex-wrap items-center justify-end gap-2 px-6 py-[10px] lg:px-8">
        {/* The agent filter (All workflows only) sits on the left; the period
            controls stay on the right under New Workflow. */}
        {!scopedAgentId && (
          <div className="relative mr-auto">
            <select
              value={agentFilter ?? ""}
              onChange={(e) => setAgentFilter(e.target.value || null)}
              aria-label="Filter by agent"
              className="h-[26px] appearance-none rounded-md border border-border bg-transparent pl-[9px] pr-6 text-[12px] font-medium text-text-primary outline-none hover:bg-surface-hover focus:border-text-muted"
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <ChevronDown size={12} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-text-muted" />
          </div>
        )}
        <InsightsRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className={cn("mx-auto flex w-full max-w-[1216px] flex-col gap-[18px] px-6 pt-[18px] pb-8 transition-opacity lg:px-8", isFetching && !isLoading && "opacity-80")}>
          <KpiStrip items={kpis} loading={isLoading && !data} />

          {/* Runs per day */}
          <section>
            <div className="mb-2 flex items-center gap-3">
              <h2 className="text-[12.5px] font-semibold text-text-primary">Runs per day</h2>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
                <span className="h-2 w-2 rounded-[2px] bg-[#10a37f]" /> Completed
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-text-secondary">
                <span className="h-2 w-2 rounded-[2px] bg-[#dc2626] dark:bg-[#ef4444]" /> Failed
              </span>
              <span className="ml-auto text-[11px] text-text-muted">
                {data ? formatDateRange(data.since, data.until) : ""}
              </span>
            </div>
            <RunsPerDayChart points={data?.perDay ?? []} loading={isLoading && !data} />
          </section>

          {/* By workflow */}
          <section>
            {/* Search sits with the table it filters, not in the controls row
                above, which sets the period for the whole page. */}
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-[12.5px] font-semibold text-text-primary">By workflow</h2>
              <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={12} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search workflows..."
                  aria-label="Search workflows by name"
                  className="h-[28px] w-80 rounded-md border border-border bg-transparent pl-7 pr-2 text-[12.5px] text-text-primary outline-none placeholder:text-text-muted hover:bg-surface-hover focus:border-text-muted"
                />
              </div>
              <WorkflowFilterButton value={filters} onChange={setFilters} activeCount={filterCount} />
              </div>
            </div>
            {isError && !data ? (
              <p className="px-[10px] py-6 text-[12px] text-danger">Couldn&rsquo;t load workflows.</p>
            ) : hasNoWorkflows && emptyState ? (
              emptyState
            ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[880px]">
                <div
                  className="grid items-center gap-3 border-b border-border px-[10px] py-2 text-[12px] font-medium text-text-muted"
                  style={{ gridTemplateColumns: GRID }}
                >
                  <SortHeader label="Workflow" col="name" sort={sort} onSort={toggleSort} />
                  <span>Status</span>
                  <span>Trigger</span>
                  <span>Agents</span>
                  <SortHeader label="Runs" col="runs" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Success" col="successRate" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Avg duration" col="avgDurationMs" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Tokens" col="tokens" sort={sort} onSort={toggleSort} align="right" />
                  <SortHeader label="Last run" col="lastRunAt" sort={sort} onSort={toggleSort} align="right" />
                  <span />
                </div>
                {isLoading && !data
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="grid items-center gap-3 border-b border-[#f0f0ee] px-[10px] py-2 dark:border-[#1a1b1e]" style={{ gridTemplateColumns: GRID }}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <div key={j} className="h-3 animate-pulse rounded bg-surface-hover" />
                        ))}
                        <span />
                      </div>
                    ))
                  : rows.length === 0
                    ? (
                      <p className="px-[10px] py-6 text-[12px] text-text-muted">
                        {searchTerm && filterCount === 0 ? (
                          <>No workflows match &ldquo;{search.trim()}&rdquo;.</>
                        ) : searchTerm || filterCount > 0 ? (
                          <>
                            No workflows match {searchTerm ? "your search and filters" : "these filters"}.{" "}
                            {filterCount > 0 && (
                              <button
                                type="button"
                                onClick={() => setFilters(NO_FILTERS)}
                                className="text-accent hover:underline"
                              >
                                Clear filters
                              </button>
                            )}
                          </>
                        ) : (
                          `No workflows${effectiveAgentId ? " for this agent" : ""} yet.`
                        )}
                      </p>
                    )
                    : rows.map((w) => {
                        const dates = rowDates?.get(w.id);
                        return (
                          // A div, not a button: the row holds the menu
                          // button, and a button cannot sit inside a button.
                          <div
                            key={w.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => openWorkflow(w.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") openWorkflow(w.id);
                            }}
                            className="group grid w-full cursor-pointer items-center gap-3 border-b border-[#f0f0ee] px-[10px] py-3 text-left transition-colors hover:rounded-md hover:bg-sidebar dark:border-[#1a1b1e]"
                            style={{ gridTemplateColumns: GRID }}
                          >
                            <span className="min-w-0">
                              <span className="block truncate whitespace-nowrap text-[13.5px] font-medium text-text-primary">{w.name}</span>
                              {dates && (
                                <span
                                  className="mt-0.5 block truncate whitespace-nowrap text-[12px] text-text-secondary"
                                  title={`Updated ${formatFullDate(dates.updatedAt)} · Created ${formatFullDate(dates.createdAt)}`}
                                >
                                  Updated {formatRelativeTime(dates.updatedAt)}
                                  <span className="mx-2" aria-hidden="true">·</span>
                                  Created {formatCreated(dates.createdAt)}
                                </span>
                              )}
                            </span>
                            <WorkflowStatus status={w.status} />
                            <span className="truncate whitespace-nowrap text-[13px] text-text-secondary">{triggerLabel(w.trigger)}</span>
                            <span className="min-w-0">
                              {w.agentIds.length > 0 ? (
                                <AgentAvatarStack
                                  agents={w.agentIds.map((id) => agentById.get(id) ?? { id, name: "?" })}
                                  size={20}
                                />
                              ) : (
                                <span className="text-[11px] text-text-muted">&mdash;</span>
                              )}
                            </span>
                            <span className="text-right text-[13.5px] tabular-nums text-text-primary">{formatInt(w.runs)}</span>
                            <SuccessBar rate={w.successRate} />
                            <span className="text-right text-[13px] tabular-nums text-text-secondary">{formatDurationMs(w.avgDurationMs)}</span>
                            <span className="text-right text-[13px] tabular-nums text-text-secondary" title={w.tokensIn + w.tokensOut > 0 ? `${formatTokens(w.tokensIn)} in · ${formatTokens(w.tokensOut)} out` : "No token telemetry for these runs"}>
                              {w.tokensIn + w.tokensOut > 0 ? formatTokens(w.tokensIn + w.tokensOut) : "—"}
                            </span>
                            <span className="text-right text-[12.5px] tabular-nums text-text-muted">{formatRelativeTime(w.lastRunAt)}</span>
                            <span className="flex justify-end">
                              {renderRowMenu?.(w, () => openInsights(w.id))}
                            </span>
                          </div>
                        );
                      })}
              </div>
            </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

const GRID = "1.9fr 76px 1.1fr 76px 54px 104px 88px 60px 66px 28px";

type WorkflowFilters = {
  status: "all" | "active" | "paused" | "draft";
  /** Created within the last N days; null for any time. */
  createdWithinDays: number | null;
};
const NO_FILTERS: WorkflowFilters = { status: "all", createdWithinDays: null };

const STATUS_FILTERS: Array<{ value: WorkflowFilters["status"]; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "draft", label: "Draft" },
];
const CREATED_FILTERS: Array<{ value: number | null; label: string }> = [
  { value: null, label: "Any time" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

/**
 * "Filter" beside the table's search box: status and created date. Choices
 * apply straight away; the button shows how many filters are on. Closes on a
 * click outside or Escape.
 */
function WorkflowFilterButton({
  value,
  onChange,
  activeCount,
}: {
  value: WorkflowFilters;
  onChange: (next: WorkflowFilters) => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const option = "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-surface-hover";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "inline-flex h-[28px] items-center gap-1.5 rounded-md border border-border px-2.5 text-[12.5px] font-medium transition-colors hover:bg-surface-hover",
          activeCount > 0 ? "text-text-primary" : "text-text-secondary",
        )}
      >
        <ListFilter size={13} />
        Filter
        {activeCount > 0 && (
          <span className="rounded bg-accent/15 px-1.5 text-[11px] tabular-nums text-accent">{activeCount}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-[220px] overflow-hidden rounded-xl border border-border bg-surface py-1 shadow-xl">
          <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium text-text-muted">Status</p>
          {STATUS_FILTERS.map((opt) => (
            <button key={opt.value} type="button" className={option} onClick={() => onChange({ ...value, status: opt.value })}>
              <span className="flex-1">{opt.label}</span>
              {value.status === opt.value && <Check size={14} className="text-accent" />}
            </button>
          ))}
          <div className="my-1 border-t border-border" />
          <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium text-text-muted">Created</p>
          {CREATED_FILTERS.map((opt) => (
            <button key={opt.label} type="button" className={option} onClick={() => onChange({ ...value, createdWithinDays: opt.value })}>
              <span className="flex-1">{opt.label}</span>
              {value.createdWithinDays === opt.value && <Check size={14} className="text-accent" />}
            </button>
          ))}
          {activeCount > 0 && (
            <>
              <div className="my-1 border-t border-border" />
              <button type="button" className={cn(option, "text-text-secondary")} onClick={() => onChange(NO_FILTERS)}>
                Clear filters
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** "14 Jul 2026" */
function formatCreated(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** "14 Jul 2026, 09:41", for the hover text. */
function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Whether the workflow is switched on: a dot and a word. */
function WorkflowStatus({ status }: { status: string }) {
  const look =
    status === "active"
      ? { dot: "bg-success", label: "Active" }
      : status === "paused"
        ? { dot: "bg-warning", label: "Paused" }
        : { dot: "bg-text-muted", label: status.charAt(0).toUpperCase() + status.slice(1) };
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] text-text-secondary">
      <span className={cn("h-2 w-2 rounded-full", look.dot)} />
      {look.label}
    </span>
  );
}

/** "vs previous 7d", or the dates when the baseline was hand-picked. */
function baselineLabel(range: InsightsRangeValue, c: { mode: string; since: string; until: string; days: number } | null | undefined): string {
  if (!c) return "";
  if (c.mode === "custom") return `vs ${formatDateRange(c.since, c.until)}`;
  return `vs prior ${range.period ?? `${c.days}d`}`;
}

/** Lower is better for cost and duration, so a fall is the good direction. */
const toneForDrop = (pct: number | null | undefined): KpiItem["subTone"] =>
  pct == null ? "muted" : pct <= 0 ? "success" : "danger";

function buildKpis(data: CompanyInsights | undefined, range: InsightsRangeValue): KpiItem[] {
  const t = data?.totals;
  const c = t?.comparison ?? null;
  const vs = baselineLabel(range, c);
  const topReason = data?.failureReasons?.[0];
  const tokensTotal = (t?.tokensIn ?? 0) + (t?.tokensOut ?? 0);
  return [
    {
      label: "Runs",
      value: formatInt(t?.runs),
      sub: c?.runsChangePct != null ? `${formatSignedPercent(c.runsChangePct)} ${vs}` : c ? `${formatInt(c.runs)} in baseline` : null,
      subTone: c?.runsChangePct != null && c.runsChangePct >= 0 ? "success" : "muted",
    },
    {
      label: "Completed",
      value: formatPercent(t?.successRate),
      sub: c?.successRateChangePts != null ? `${formatSignedPercent(c.successRateChangePts)} pts ${vs}` : t ? `${formatInt(t.completed)} runs` : null,
      subTone: c?.successRateChangePts != null ? (c.successRateChangePts >= 0 ? "success" : "danger") : "muted",
    },
    {
      label: "Failed",
      value: formatInt(t?.failed),
      sub: topReason && topReason.kind !== "cancelled" ? `${formatInt(topReason.count)} ${topReason.label}` : t && t.cancelled > 0 ? `${formatInt(t.cancelled)} cancelled` : t ? "no failures" : null,
      subTone: t && t.failed > 0 ? "danger" : "muted",
    },
    {
      label: "Agent hours",
      value: formatHours(t?.durationMsTotal),
      sub:
        c?.avgDurationChangePct != null && t?.avgDurationMs != null
          ? `avg ${formatDurationMs(t.avgDurationMs)}, ${formatSignedPercent(c.avgDurationChangePct)} ${vs}`
          : t?.avgDurationMs != null
            ? `avg ${formatDurationMs(t.avgDurationMs)} per run`
            : null,
      subTone: c?.avgDurationChangePct != null ? toneForDrop(c.avgDurationChangePct) : "muted",
    },
    {
      label: "Tokens",
      value: tokensTotal > 0 ? formatTokens(tokensTotal) : t ? "—" : "",
      sub:
        tokensTotal > 0 && c?.tokensPerRunChangePct != null
          ? `${formatSignedPercent(c.tokensPerRunChangePct)} per run ${vs}`
          : tokensTotal > 0
            ? `+ ${formatTokens(t!.cacheReadTokens + t!.cacheWriteTokens)} cached`
            : t
              ? "not tracked for these runs"
              : null,
      subTone: tokensTotal > 0 && c?.tokensPerRunChangePct != null ? toneForDrop(c.tokensPerRunChangePct) : "muted",
      title:
        tokensTotal === 0
          ? "No telemetry rows attributed to workflow runs in this period"
          : `${formatTokens(t!.tokensIn)} in · ${formatTokens(t!.tokensOut)} out · ${formatTokens(t!.cacheReadTokens)} cache read · ${formatTokens(t!.cacheWriteTokens)} cache write`,
    },
    {
      label: "Cost",
      value: t?.priced ? formatCostCents(t.costCents) : t ? "—" : "",
      sub:
        t?.costPerRunCents != null && c?.costPerRunChangePct != null
          ? `${formatCostCents(t.costPerRunCents)}/run, ${formatSignedPercent(c.costPerRunChangePct)} ${vs}`
          : t?.costPerRunCents != null
            ? `${formatCostCents(t.costPerRunCents)} per run`
            : t
              ? "no priced usage"
              : null,
      subTone: c?.costPerRunChangePct != null ? toneForDrop(c.costPerRunChangePct) : "muted",
      title:
        c?.costChangePct != null
          ? `Total cost ${formatSignedPercent(c.costChangePct)} against the baseline window`
          : undefined,
    },
  ];
}

function SortHeader<T extends string>({
  label,
  col,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  col: T;
  sort: { col: T; dir: "asc" | "desc" };
  onSort: (col: T) => void;
  align?: "left" | "right";
}) {
  const active = sort.col === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap font-medium transition-colors hover:text-text-primary",
        align === "right" && "justify-end",
        active && "text-text-primary",
      )}
    >
      {label}
      {active && (sort.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
    </button>
  );
}

export function SuccessBar({ rate }: { rate: number | null }) {
  if (rate == null) return <span className="text-[12.5px] text-text-muted">—</span>;
  const tone = rate < 75 ? "bg-[#dc2626] dark:bg-[#ef4444]" : rate < 90 ? "bg-[#d97706] dark:bg-[#f59e0b]" : "bg-[#10a37f]";
  const text = rate < 75 ? "text-[#dc2626] dark:text-[#ef4444]" : rate < 90 ? "text-[#d97706] dark:text-[#f59e0b]" : "text-text-primary";
  return (
    <span className="flex items-center gap-2">
      <span className="h-1 flex-1 overflow-hidden rounded-[2px] bg-border">
        <span className={cn("block h-full rounded-[2px] transition-[width] duration-200", tone)} style={{ width: `${Math.max(2, Math.min(100, rate))}%` }} />
      </span>
      <span className={cn("min-w-[40px] text-right text-[12.5px] tabular-nums", text)}>{formatPercent(rate)}</span>
    </span>
  );
}
